import { useState, useEffect, useMemo, useCallback } from 'react';
import schedulesService from '../services/schedulesService';
import cascadeDeleteService from '../services/cascadeDeleteService';
import placeServicesService from '../services/placeServicesService';
import placeServiceProfessionalsService from '../services/placeServiceProfessionalsService';
import entitiesService from '../services/entitiesService';
import { useSchedules } from '../hooks/queries';
import { useQueryClient } from '@tanstack/react-query';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';
import AppSnackbar from '../components/ui/AppSnackbar';
import { useLocation } from 'react-router-dom';
import CascadeDeleteDialog from '../components/ui/CascadeDeleteDialog';
import ScheduleForm from '../components/forms/ScheduleForm';
import ShareScheduleDialog from '../components/ShareScheduleDialog';
import ShiftForm from '../components/ShiftForm';
import SmartWizard from '../components/wizard/SmartWizard';
import AppointmentFormDialog from '../components/AppointmentFormDialog';
import { trackEvent, trackError } from '../utils/tracking';
// View do Claude Design (só lê vm / dispara actions) + o builder do contrato @v1.
import SchedulesView from './schedules/SchedulesView';
import { buildSchedulesVm } from './schedules/buildSchedulesVm';

export default function SchedulesPage() {
  const location = useLocation();
  const queryClient = useQueryClient();

  // ✅ Custom Hooks - React Query
  const { 
    schedules, 
    professionals, 
    places, 
    loading, 
    error, 
    reload, 
    create, 
    update, 
  } = useSchedules();
  
  const formDialog = useDialog();
  const deleteDialog = useDialog();
  const shiftDialog = useDialog();
  const shareDialog = useDialog();
  const { snackbar, showSuccess, showError, showWarning, hideSnackbar } = useSnackbar();

  // Estados adicionais específicos de schedules
  const [ScheduleShifts, setAgendaShifts] = useState({});
  // Espelho de entidades (serviços + vínculos) carregado ao abrir o ShiftForm, para oferecer
  // TODOS os serviços do usuário e permitir vincular "pela culatra" ao salvar.
  const [shiftEntities, setShiftEntities] = useState({
    services: [], placeServices: [], placeServiceProfessionals: [], places: [], professionals: [],
  });
  const [shareUrl, setShareUrl] = useState('');
  
  // Estado para wizard de criação de agenda (SmartWizard unificado — config add-schedule)
  const [showSmartWizard, setShowSmartWizard] = useState(false);
  
  // Estado para diálogo de agendamento
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [appointmentPrefillConfig, setAppointmentPrefillConfig] = useState(null);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProfessional, setFilterProfessional] = useState('');
  const [filterPlace, setFilterPlace] = useState('');
  const [filterStatus, setFilterStatus] = useState(''); // '' = todos, exceto excluídas

  // ── Qual wizard abrir (B-037) ────────────────────────────────────────────
  // O config 'add-schedule' tem os passos de Locais e Profissionais em mode:'select-only' —
  // só dá para ESCOLHER entre os existentes, não criar. Numa conta sem local ele abriria num
  // seletor vazio, sem saída; foi por isso que o botão vivia desabilitado, tapando o buraco.
  // 'setup-completo' tem Profissionais, Serviços e Locais em 'select-or-create' e termina em
  // ScheduleStep — cria tudo do zero. Então em vez de desabilitar, trocamos o wizard.
  const semLocal = (places?.length ?? 0) === 0;
  const wizardId = semLocal ? 'setup-completo' : 'add-schedule';
  // Abrir wizard via querystring (?wizard=1 ou ?wizard=true) -> abre seletor de local
  // ?busca=<nome> chega já filtrado — é por onde Locais manda o dono para a agenda com problema.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const wizardParam = params.get('wizard');
    const busca = params.get('busca');
    if (busca) setSearchTerm(busca);
    if (wizardParam === '1' || wizardParam === 'true') setShowSmartWizard(true);
    if (busca || wizardParam === '1' || wizardParam === 'true') {
      window.history.replaceState(null, '', location.pathname);
    }
  }, [location.pathname, location.search]);

  // Carregar turnos quando schedules mudar
  useEffect(() => {
    if (schedules?.length > 0) {
      loadShiftsForAgendas(schedules);
    }
  }, [schedules]);

  const loadShiftsForAgendas = async (SchedulesData) => {
    try {
      const shiftsMap = {};
      await Promise.all(
        SchedulesData.map(async (schedule) => {
          try {
            const shifts = await schedulesService.getShifts(schedule.id, schedule.status?.toLowerCase() === 'deleted');
            shiftsMap[schedule.id] = shifts;
          } catch (error) {
            console.error(`Erro ao carregar turnos da schedule ${schedule.id}:`, error);
            shiftsMap[schedule.id] = [];
          }
        })
      );
      setAgendaShifts(shiftsMap);
    } catch {
      console.error('Erro ao carregar turnos:', error);
    }
  };

  // ✅ Handlers de CRUD
  // ✅ Callbacks memoizados para cards
  const handleEditAgenda = useCallback((schedule) => {
    formDialog.handleOpen(schedule);
  }, [formDialog]);

  const handleDeleteAgenda = useCallback((schedule) => {
    deleteDialog.handleOpen(schedule);
  }, [deleteDialog]);

  // Pausar/reativar a agenda inteira. É a ação mais consequente da tela — enquanto pausada,
  // nenhuma página consegue marcar horário nela —, então o retorno diz o que aconteceu em vez
  // de um "salvo com sucesso" genérico.
  const handleToggleLifecycle = useCallback(async (schedule) => {
    try {
      await schedulesService.toggleActive(schedule.id);
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      const estavaAtiva = String(schedule.status || 'Active').toLowerCase() === 'active';
      showSuccess(estavaAtiva
        ? `"${schedule.name}" foi pausada e parou de receber agendamentos.`
        : `"${schedule.name}" voltou a receber agendamentos.`);
    } catch (err) {
      showError('Não foi possível alterar a agenda: ' + (err.response?.data?.message || err.message));
    }
  }, [queryClient, showSuccess, showError]);

  const handleShareAgenda = useCallback((schedule) => {
    const shifts = ScheduleShifts[schedule.id] || [];
    
    if (shifts.length === 0) {
      showWarning('Esta agenda precisa ter turnos e horários configurados para poder ser compartilhada.');
      return;
    }
    
    const url = `https://share.agendario.com.br/schedule/${schedule.id}`;
    setShareUrl(url);
    shareDialog.handleOpen(schedule);
  }, [ScheduleShifts, showWarning, shareDialog]);

  const handleCreate = async (data) => {
    try {
      const result = await create(data);
      formDialog.handleClose();
      
      const professionalName = professionals.find(p => p.id === data.professionalId)?.name;
      const placeName = places.find(p => p.id === data.placeId)?.name;
      
      trackEvent('agenda_created', {
        agenda_id: result.id || 'unknown',
        professional_name: professionalName,
        place_name: placeName,
        max_booking_days_ahead: data.maxBookingDaysAhead,
        max_available_days_to_show: data.maxAvailableDaysToShow
      });
      
      showSuccess('Agenda criada com sucesso!');
      await loadShiftsForAgendas(schedules);
    } catch (err) {
      trackError('agenda_save_error', err, {
        is_update: false,
        professional_id: data.professionalId,
        place_id: data.placeId
      });
      showError('Erro ao criar agenda: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdate = async (data) => {
    try {
      await update(formDialog.data.id, data);
      formDialog.handleClose();
      
      const professionalName = professionals.find(p => p.id === data.professionalId)?.name;
      const placeName = places.find(p => p.id === data.placeId)?.name;
      
      trackEvent('agenda_updated', {
        agenda_id: formDialog.data.id,
        professional_name: professionalName,
        place_name: placeName,
        max_booking_days_ahead: data.maxBookingDaysAhead
      });
      
      showSuccess('Agenda atualizada com sucesso!');
    } catch (err) {
      trackError('agenda_save_error', err, {
        is_update: true,
        agenda_id: formDialog.data.id
      });
      showError('Erro ao atualizar agenda: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = formDialog.isEdit ? handleUpdate : handleCreate;

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      showSuccess('Link copiado com sucesso!');
    } catch {
      showError('Erro ao copiar link');
    }
  };

  const handleOpenUrl = () => {
    window.open(shareUrl, '_blank');
  };

  // ✅ Handlers de Turnos (Shifts)
  const handleOpenShift = async (schedule, shift = null) => {
    try {
      // ✅ NORMALIZADO: schedule agora tem placeId e professionalId diretamente
      const placeId = schedule.placeId;
      const professionalId = schedule.professionalId;
      const placeProfessionalId = schedule.placeProfessionalId;
      
      if (!placeId) {
        showError('Erro: Local não identificado na agenda');
        return;
      }
      
      if (!professionalId) {
        showError('Erro: Profissional não identificado na agenda');
        return;
      }
      
      // Espelho completo das entidades do usuário (serviços + vínculos) para o ShiftForm.
      // Assim ele oferece TODOS os serviços — inclusive os não vinculados a este local/profissional —
      // e o vínculo é criado "pela culatra" no submit.
      let placeName = schedule.placeName || '';
      try {
        const data = await entitiesService.getAll();
        setShiftEntities({
          services: data?.services || [],
          placeServices: data?.placeServices || [],
          placeServiceProfessionals: data?.placeServiceProfessionals || [],
          places: data?.places || [],
          professionals: data?.professionals || [],
        });
        if (!placeName) {
          placeName = (data?.places || []).find(p => p.id === placeId)?.name || '';
        }
      } catch (entitiesError) {
        console.warn('[SchedulesPage] Falha ao carregar entidades para o ShiftForm:', entitiesError);
        setShiftEntities({ services: [], placeServices: [], placeServiceProfessionals: [], places: [], professionals: [] });
      }

      shiftDialog.handleOpen({
        ...shift,
        ScheduleId: schedule.id,
        schedule,
        placeId,
        professionalId,
        placeProfessionalId,
        placeName,
      });
    } catch (error) {
      console.error('Erro ao abrir turno:', error);
      showError('Erro ao abrir turno.');
    }
  };

  const handleShiftSubmit = async (shiftData) => {
    try {
      const { ScheduleId, id: shiftId, placeId, placeProfessionalId } = shiftDialog.data;

      // "Pela culatra": garante que todo serviço marcado esteja vinculado a ESTE profissional
      // neste local (place_service + place_service_professional) ANTES de gravar o turno.
      // Se algum vínculo falhar, aborta o save (não deixa turno meio-vinculado).
      if (placeId && placeProfessionalId) {
        const existingPsp = shiftEntities.placeServiceProfessionals || [];
        const existingPs = shiftEntities.placeServices || [];
        for (const serviceId of (shiftData.serviceIds || [])) {
          const boundHere = existingPsp.some(
            psp => psp.placeProfessionalId === placeProfessionalId && psp.serviceId === serviceId
          );
          if (boundHere) continue;

          // 1) place_service (serviço oferecido no local)
          let placeServiceId = existingPs.find(ps => ps.placeId === placeId && ps.serviceId === serviceId)?.id;
          if (!placeServiceId) {
            const createdPs = await placeServicesService.create(placeId, { serviceId });
            placeServiceId = createdPs?.id;
          }
          if (!placeServiceId) {
            throw new Error('Não foi possível vincular o serviço ao local.');
          }

          // 2) place_service_professional (profissional oferece o serviço no local)
          await placeServiceProfessionalsService.create(placeId, placeServiceId, { placeProfessionalId });
        }
      }

      if (shiftId) {
        await schedulesService.updateShift(ScheduleId, shiftId, shiftData);
        queryClient.invalidateQueries({ queryKey: ['schedules'] });
        showSuccess('Turno atualizado com sucesso!');
      } else {
        await schedulesService.createShift(ScheduleId, shiftData);
        queryClient.invalidateQueries({ queryKey: ['schedules'] });
        showSuccess('Turno criado com sucesso!');
      }

      shiftDialog.handleClose();
      await loadShiftsForAgendas(schedules);
    } catch (error) {
      showError('Erro ao salvar turno: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleShiftDelete = async (shiftId) => {
    // Turno ainda não persistido (form aberto para um turno sem id): nada a excluir.
    // Evita DELETE /schedules/{id}/shifts/undefined → 400.
    if (!shiftId) {
      shiftDialog.handleClose();
      return;
    }
    try {
      const ScheduleId = shiftDialog.data.ScheduleId;
      const path = `/schedules/${ScheduleId}/shifts/${shiftId}`;
      const impact = await cascadeDeleteService.getImpact(path);
      const confirmed = window.confirm(
        `Tem certeza que deseja excluir este turno?\n\n` +
        (impact.impactGroups?.length > 0
          ? `Isso afetará:\n${impact.impactGroups.map(g => `• ${g.displayName}: ${g.count} registros`).join('\n')}`
          : 'Isso afetará os registros relacionados.')
      );
      if (confirmed) {
        await cascadeDeleteService.confirmDelete(path, impact.confirmHash);
        queryClient.invalidateQueries({ queryKey: ['schedules'] });
        showSuccess('Turno excluído com sucesso!');
        shiftDialog.handleClose();
        await loadShiftsForAgendas(schedules);
      }
    } catch (error) {
      if (error.response?.status === 409) {
        showError('A confirmação expirou. Por favor, tente novamente.');
      } else {
        showError('Erro ao excluir turno: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterProfessional('');
    setFilterPlace('');
    setFilterStatus('');
  };

  // ✅ Handlers para wizard de criação de agenda
  const handleSmartWizardSuccess = useCallback(() => {
    setShowSmartWizard(false);
    showSuccess('Agenda criada com sucesso!');
    reload(); // Recarregar lista de agendas
  }, [showSuccess, reload]);

  // Handler para abrir diálogo de agendamento a partir de um schedule
  const handleOpenAppointmentFromSchedule = useCallback((schedule) => {
    setAppointmentPrefillConfig({
      professionalId: schedule.professionalId,
      placeId: schedule.placeId,
    });
    setAppointmentDialogOpen(true);
  }, []);

  // Handler para fechar diálogo de agendamento
  const handleCloseAppointmentDialog = useCallback(() => {
    setAppointmentDialogOpen(false);
    setAppointmentPrefillConfig(null);
  }, []);

  // Handler para sucesso do agendamento
  const handleAppointmentSuccess = useCallback(() => {
    showSuccess('Agendamento criado com sucesso!');
    setAppointmentDialogOpen(false);
    setAppointmentPrefillConfig(null);
  }, [showSuccess]);


  // ── VM + actions (contrato schedules.vm.js @v1) ───────────────────────────
  // A View é do Claude Design e só lê `vm` / dispara `actions`. Toda a regra — inclusive o
  // veredito de operação, que NÃO é derivável do status — mora em buildSchedulesVm.
  // O carregamento da tela são DUAS etapas: a lista de agendas e, só depois, os turnos de cada
  // uma. `loading` do useSchedules termina na primeira — e é nessa brecha que a tela chegava a
  // acusar todo mundo de não receber agendamento, porque ainda não tinha os turnos na mão.
  // Só está pronto quando toda agenda já tem entrada no mapa (mesmo que seja um array vazio).
  const turnosCarregados = useMemo(
    () => !schedules?.length || schedules.every((s) => ScheduleShifts[s.id] !== undefined),
    [schedules, ScheduleShifts]
  );

  const vm = useMemo(() => buildSchedulesVm({
    schedules,
    shiftsByScheduleId: ScheduleShifts,
    professionals,
    places,
    filters: { searchText: searchTerm, professional: filterProfessional, place: filterPlace, status: filterStatus },
    loading: loading || !turnosCarregados,
    error,
    hasPlaces: (places?.length ?? 0) > 0,
  }), [schedules, ScheduleShifts, professionals, places, searchTerm, filterProfessional, filterPlace, filterStatus, loading, turnosCarregados, error]);

  const acharAgenda = useCallback((id) => (schedules || []).find((s) => s.id === id), [schedules]);

  // Objeto simples, não memoizado: `SchedulesView` não é `memo()`, então identidade estável não
  // evitaria render nenhum — e metade dos handlers é recriada a cada render de qualquer forma.
  const actions = {
    onNew: () => setShowSmartWizard(true),
    onEdit: (id) => { const s = acharAgenda(id); if (s) handleEditAgenda(s); },
    onDelete: (id) => { const s = acharAgenda(id); if (s) handleDeleteAgenda(s); },
    onShare: (id) => { const s = acharAgenda(id); if (s) handleShareAgenda(s); },
    onAddShift: (id) => { const s = acharAgenda(id); if (s) handleOpenShift(s); },
    onEditShift: (scheduleId, shiftId) => {
      const s = acharAgenda(scheduleId);
      if (!s) return;
      const shift = (ScheduleShifts[scheduleId] || []).find((t) => t.id === shiftId);
      handleOpenShift(s, shift || null);
    },
    onBook: (id) => { const s = acharAgenda(id); if (s) handleOpenAppointmentFromSchedule(s); },
    onToggleLifecycle: (id) => { const s = acharAgenda(id); if (s) handleToggleLifecycle(s); },
    // Resolve um item da trilha sem o dono ter de caçar o botão certo. 'editShift' abre o turno
    // que está com problema (sem serviço ou desligado) — não o primeiro da lista, que pode estar
    // perfeito e deixaria a pessoa sem entender o que era para mudar.
    onChecklistAction: (scheduleId, actionId) => {
      const s = acharAgenda(scheduleId);
      if (!s) return;
      if (actionId === 'addShift') return handleOpenShift(s);
      if (actionId === 'edit') return handleEditAgenda(s);
      if (actionId === 'editShift') {
        const turnos = ScheduleShifts[scheduleId] || [];
        const problema = turnos.find((t) => !(t.services || []).some((x) => x.serviceName))
          || turnos.find((t) => !t.isActive)
          || turnos[0];
        return handleOpenShift(s, problema || null);
      }
    },
    onSearchChange: setSearchTerm,
    onProfessionalChange: setFilterProfessional,
    onPlaceChange: setFilterPlace,
    onStatusChange: setFilterStatus,
    onClearFilters: clearFilters,
    onEmptyAction: (actionId) => {
      if (actionId === 'clearFilters') return clearFilters();
      return setShowSmartWizard(true);   // 'setup' e 'create' abrem o wizard (que já escolhe qual)
    },
    onRetry: reload,
  };

  return (
    <>
      <SchedulesView vm={vm} actions={actions} />

      {/* schedule Form Dialog */}
      <ScheduleForm
        open={formDialog.open}
        onClose={formDialog.handleClose}
        onSubmit={handleSubmit}
        data={formDialog.data}
        professionals={professionals}
        places={places}
      />

      {/* Delete Confirmation Dialog */}
      <CascadeDeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onDeleted={() => {
          deleteDialog.handleClose();
          showSuccess('Excluído com sucesso!');
          reload();
        }}
        deletePath={`/schedules/${deleteDialog.data?.id}`}
        entityName={deleteDialog.data?.name}
      />

      {/* Share Dialog */}
      <ShareScheduleDialog
        open={shareDialog.open}
        onClose={shareDialog.handleClose}
        shareUrl={shareUrl}
        onCopy={handleCopyUrl}
        onOpen={handleOpenUrl}
      />

      {/* Shift Form Dialog */}
      <ShiftForm
        open={shiftDialog.open}
        onClose={shiftDialog.handleClose}
        onSubmit={handleShiftSubmit}
        onDelete={handleShiftDelete}
        shift={shiftDialog.data}
        ScheduleId={shiftDialog.data?.ScheduleId}
        context={{
          placeId: shiftDialog.data?.placeId,
          professionalId: shiftDialog.data?.professionalId,
          placeProfessionalId: shiftDialog.data?.placeProfessionalId,
          placeName: shiftDialog.data?.placeName,
        }}
        services={shiftEntities.services}
        placeServices={shiftEntities.placeServices}
        placeServiceProfessionals={shiftEntities.placeServiceProfessionals}
        places={shiftEntities.places}
        professionals={shiftEntities.professionals}
      />

      {/* Wizard unificado de criação de agenda (SmartWizard).
          Substitui o antigo ScheduleCreationWizard + seletor de local: o próprio wizard
          escolhe local → profissional → serviços → vínculos → agenda, garantindo que o
          turno nasça com serviço vinculado.

          O config varia com o estado da conta (B-037): 'add-schedule' pressupõe local e
          profissional já existentes (passos em mode:'select-only'); quem não tem local vai
          para 'setup-completo', que cria tudo do zero ('select-or-create'). */}
      <SmartWizard
        open={showSmartWizard}
        wizardId={wizardId}
        onClose={() => setShowSmartWizard(false)}
        onSuccess={handleSmartWizardSuccess}
      />

      {/* Diálogo de Agendamento - auto-suficiente, carrega próprios dados */}
      <AppointmentFormDialog
        open={appointmentDialogOpen}
        onClose={handleCloseAppointmentDialog}
        onSuccess={handleAppointmentSuccess}
        prefillConfig={appointmentPrefillConfig}
      />

      {/* Snackbar */}
      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </>
  );
}
